import { useCallback, useId, useMemo, useRef, useState, useSyncExternalStore } from "react";
import {
  ACCOUNTS_TABLE,
  ALL_ACCOUNTS,
  FILLS_TABLE,
  ORDERS_TABLE,
  OrderState,
  OrderType,
  POSITIONS_TABLE,
  Side,
  accountsForView,
  filterByAccountScope,
  formatTime,
  isWorkingOrder,
  resolveAccountScope,
  showsAccountColumn,
  sumCents,
  useSettings,
  zoneAbbreviation,
  type AccountFacts,
  type AccountFilter,
  type AccountScope,
  type AccountsViewMode,
  type ColumnDef,
  type ColumnState,
  type FillEvent,
  type FillFacts,
  type OrderEvent,
  type OrderFacts,
  type PositionFacts,
  type TableDef,
} from "@helm/core";
import { useDisplayZone } from "../time/useDisplayZone";
import { formatCents, formatTicks, pnlTone } from "@helm/ui";
import { Tabs, tabPanelProps } from "../components/Tabs";
import {
  AccountFlattenButton,
  ExitAllButton,
  FlattenAllButton,
  OrderCancelButton,
  PositionCloseButton,
} from "../trading/TableActions";
import {
  sameSelection,
  selectionSlice,
  useSelectionSlice,
  useTradingView,
  useTradingViewSlice,
  type AccountList,
  type SelectionSlice,
  type TradingView,
} from "../viewmodel/account";
import { settingsSync } from "../session";
import { Icon } from "../components/Icon";
import { marketData, priceFormatOf } from "../md/marketData";
import { ColumnPicker } from "../tables/ColumnPicker";
import { DataTable } from "../tables/DataTable";
import { formatCell, type CellCtx, type CellOut } from "../tables/cells";
import { accountFacts, fillFacts, orderFacts, positionFacts, priceFormatForContract } from "../tables/facts";
import {
  tableParamOf,
  useColumnState,
  useNow,
  withTableParam,
  type TablesParam,
} from "../tables/useTableColumns";

/**
 * Account tables (Tradesea look: Accounts, Orders, Positions, Fills, Notifications) with the
 * reference's column names and empty states (docs/DESIGN-REFERENCE.md). Each is its own movable
 * module. Positions / Orders / Fills carry an account filter like Tradovate's modules (follow the
 * top bar, all accounts, main, or one account) and add an Account column when several accounts
 * show; the Accounts module toggles "Main account only / All accounts". Rows come from the
 * trading stream (`useTradingView`). Row and toolbar actions (Cancel, Exit = Exit at Mkt & Cxl,
 * Flatten, Exit All) are live on the mock / Rithmic Test only (ADR-0021, trading/TableActions).
 *
 * Positions, Accounts, Orders and Fills columns are adjustable (owner 2026-09-27): the column
 * registries live in `@helm/core` tables, the grid, picker and templates in `../tables`, and each
 * panel's choice is saved in its params (`WidgetParams.tables`).
 */

// ---- account filter (shared) -----------------------------------------------------------------

function accountLabel(tv: { accounts: AccountList }, id: string | null, privacy: boolean): string {
  if (id === null) return "—";
  if (privacy) return "***";
  return tv.accounts.find((a) => a.summary.id === id)?.summary.label ?? id;
}

function scopeOf(tv: SelectionSlice, filter: AccountFilter): { scope: AccountScope; multi: boolean } {
  const ids = tv.accounts.map((a) => a.summary.id);
  const scope = resolveAccountScope(filter, tv.selection, tv.mainId, ids);
  return { scope, multi: showsAccountColumn(scope, ids.length) };
}

/** Orders and the selection: quotes and RiskState frames leave the Orders table alone. */
type OrdersSlice = SelectionSlice & Pick<TradingView, "orders"> & { bookCurrent: boolean };
const ordersSlice = (v: TradingView): OrdersSlice => ({
  ...selectionSlice(v),
  orders: v.orders,
  bookCurrent: v.accounts.length > 0 && v.accounts.every((a) => a.workingOrders !== null),
});
const sameOrdersSlice = (a: OrdersSlice, b: OrdersSlice): boolean =>
  a.orders === b.orders && a.bookCurrent === b.bookCurrent && sameSelection(a, b);

/** Fills and the selection, for the Fills table. */
type FillsSlice = SelectionSlice & Pick<TradingView, "fills">;
const fillsSlice = (v: TradingView): FillsSlice => ({ ...selectionSlice(v), fills: v.fills });
const sameFillsSlice = (a: FillsSlice, b: FillsSlice): boolean => a.fills === b.fills && sameSelection(a, b);

/** Which accounts a module shows. Viewing only. */
export function AccountFilterSelect({
  filter,
  onChange,
}: {
  filter: AccountFilter;
  onChange: (f: AccountFilter) => void;
}): React.JSX.Element {
  const tv = useSelectionSlice();
  const privacy = useSettings((s) => s.settings.privacy);
  const follow = tv.selection === ALL_ACCOUNTS ? "All accounts" : accountLabel(tv, tv.activeId, privacy);
  const pinned = filter.startsWith("account:") ? filter.slice("account:".length) : null;
  const missing = pinned !== null && !tv.accounts.some((a) => a.summary.id === pinned);
  return (
    <select
      className="select select-sm acct-filter"
      aria-label="Accounts shown in this module"
      title="Accounts shown in this module (follow the top bar, all, main, or one account)"
      value={filter}
      onChange={(e) => {
        onChange(e.target.value as AccountFilter);
      }}
    >
      <option value="global">Top bar · {follow}</option>
      <option value="all">All accounts</option>
      <option value="main">Main · {accountLabel(tv, tv.mainId, privacy)}</option>
      {tv.accounts.map((a) => (
        <option key={a.summary.id} value={`account:${a.summary.id}`}>
          {privacy ? "***" : a.summary.label}
        </option>
      ))}
      {missing && <option value={filter}>{pinned} (not connected)</option>}
    </select>
  );
}

/** Empty-state text when a module is pinned to an account that is not connected. */
function missingText(scope: AccountScope): string | null {
  return scope.kind === "none" && scope.missing !== null
    ? `Account ${scope.missing} is not connected.`
    : null;
}

function EmptyRow({ span, title, text }: { span: number; title: string; text: string }): React.JSX.Element {
  return (
    <tr className="grid-empty">
      <td colSpan={span}>
        <div className="empty-inline">
          <span className="empty-title">{title}</span>
          <span className="muted">{text}</span>
        </div>
      </td>
    </tr>
  );
}

/**
 * Fixed column headers (Notifications; the trading tables use `DataTable`). Time columns carry
 * the display-zone label ("Time KST").
 */
function Head({ cols }: { cols: readonly string[] }): React.JSX.Element {
  const zone = useDisplayZone();
  const label = zoneAbbreviation(zone);
  return (
    <thead>
      <tr>
        {cols.map((c) => (
          <th key={c} scope="col">
            {/\btime\b/i.test(c) ? `${c} ${label}` : c}
          </th>
        ))}
      </tr>
    </thead>
  );
}

// ---- formatting helpers (display edge) ------------------------------------------------------

/** A contract's price in ticks as text; raw ticks when its instrument is not loaded. */
function priceText(ticks: bigint, contract: string): string {
  if (ticks === 0n) return "—";
  const fmt = priceFormatOf(marketData.findByContract(contract)?.info ?? null);
  return fmt ? formatTicks(Number(ticks), fmt) : `${ticks.toString()} t`;
}

/**
 * Working-order prices after the type word: "@ limit", a stop-only order "@ trigger" (so a stop
 * reads "stop @ 21,400.00", not "stop stop 21,400.00"), a stop limit "@ limit, trigger X".
 */
function workingPrices(limitTicks: bigint, stopTicks: bigint, contract: string): string {
  if (limitTicks !== 0n && stopTicks !== 0n)
    return `@ ${priceText(limitTicks, contract)}, trigger ${priceText(stopTicks, contract)}`;
  if (limitTicks !== 0n) return `@ ${priceText(limitTicks, contract)}`;
  if (stopTicks !== 0n) return `@ ${priceText(stopTicks, contract)}`;
  return "";
}

const SIDE: Partial<Record<Side, string>> = { [Side.BUY]: "Buy", [Side.SELL]: "Sell" };

const ORDER_TYPE: Partial<Record<OrderType, string>> = {
  [OrderType.MARKET]: "Market",
  [OrderType.LIMIT]: "Limit",
  [OrderType.STOP_MARKET]: "Stop",
  [OrderType.STOP_LIMIT]: "Stop Limit",
  [OrderType.TRAILING_STOP]: "Trailing Stop",
  [OrderType.MIT]: "MIT",
};

const ORDER_STATE: Partial<Record<OrderState, string>> = {
  [OrderState.DRAFT]: "Draft",
  [OrderState.PENDING_SUBMIT]: "Pending",
  [OrderState.WORKING]: "Working",
  [OrderState.PARTIALLY_FILLED]: "Partial",
  [OrderState.FILLED]: "Filled",
  [OrderState.PENDING_CANCEL]: "Cancelling",
  [OrderState.CANCELLED]: "Cancelled",
  [OrderState.PENDING_MODIFY]: "Modifying",
  [OrderState.REJECTED]: "Rejected",
  [OrderState.TRIGGERED]: "Triggered",
  [OrderState.UNKNOWN]: "Unknown — reconciling",
  [OrderState.NOT_PLACED]: "Not placed",
};

// ---- column system (shared) ------------------------------------------------------------------

/**
 * Column state for the tables in one panel (owner 2026-09-27 "make the columns adjustable with a
 * table template"): `tables` is the panel's persisted map, `onTablesChange` saves it with the
 * workspace. Without them (e.g. mobile) each table keeps its columns in component state.
 */
export type TableColumnsProps = {
  tables?: unknown;
  onTablesChange?: (t: TablesParam) => void;
};

function useTable<R>(
  def: TableDef<R>,
  { tables, onTablesChange }: TableColumnsProps,
): [ColumnState, (s: ColumnState) => void] {
  // One setter for the table's life (it writes into the latest params), so the memoized column
  // picker and header do not re-render with every render of the table.
  const latest = useRef({ tables, onTablesChange });
  latest.current = { tables, onTablesChange };
  const saved = onTablesChange !== undefined;
  const onChange = useMemo(
    () =>
      saved
        ? (s: ColumnState | null): void => {
            const l = latest.current;
            l.onTablesChange?.(withTableParam(l.tables, def.id, s));
          }
        : undefined,
    [saved, def.id],
  );
  return useColumnState(def, tableParamOf(tables, def.id), onChange);
}

const subscribeFormats = (l: () => void): (() => void) => marketData.onAnyChange(l);
const formatsVersion = (): number => marketData.formatsVersion;

const priceFormatOfRow = (r: { symbol: string }): ReturnType<typeof priceFormatForContract> =>
  priceFormatForContract(r.symbol);

function useCellCtx<R extends { symbol: string }>(): CellCtx<R> {
  const privacy = useSettings((s) => s.settings.privacy);
  const zone = useDisplayZone();
  // Memoized rows re-render only when their ctx changes: a new ctx per instrument-format change,
  // so a price shown as raw ticks until its contract's facts load is re-formatted then.
  const formats = useSyncExternalStore(subscribeFormats, formatsVersion);
  return useMemo(() => ({ privacy, zone, priceFormat: priceFormatOfRow, formats }), [privacy, zone, formats]);
}

// ---- Accounts --------------------------------------------------------------------------------

const ACCOUNTS_VIEWS = [
  ["main", "Main account only"],
  ["all", "All accounts"],
] as const;

/**
 * Accounts module (Tradovate's Accounts grid, Tradesea columns by default; adjustable): every
 * account or just the main one; ★ marks / sets the main account (synced setting). Row click shows
 * that account in the top bar. Flatten acts on that row's account; Flatten All follows its separate confirmation preference.
 */
export function AccountsTable({
  mode = "all",
  onModeChange,
  onShowAccount,
  ...cols
}: {
  mode?: AccountsViewMode;
  onModeChange?: (m: AccountsViewMode) => void;
  onShowAccount?: (id: string) => void;
} & TableColumnsProps): React.JSX.Element {
  const id = useId();
  const tv = useTradingView();
  const privacy = useSettings((s) => s.settings.privacy);
  const zone = useDisplayZone();
  const [state, setState] = useTable(ACCOUNTS_TABLE, cols);
  const shown = new Set(
    accountsForView(
      mode,
      tv.mainId,
      tv.accounts.map((a) => a.summary.id),
    ),
  );
  const rows = accountFacts(
    tv,
    tv.accounts.filter((a) => shown.has(a.summary.id)),
  );
  const selectedId = tv.selection === ALL_ACCOUNTS ? null : tv.activeId;
  const ctx = useMemo<CellCtx<AccountFacts>>(
    () => ({ privacy, zone, priceFormat: () => null }),
    [privacy, zone],
  );
  const rowProps = useCallback(
    (a: AccountFacts): React.HTMLAttributes<HTMLTableRowElement> => ({
      className: a.accountId === selectedId ? "row-active" : undefined,
      tabIndex: onShowAccount ? 0 : undefined,
      title: onShowAccount ? "Show this account in the top bar" : undefined,
      onClick: (e) => {
        // Buttons in the row (main star, disabled Flatten) do not select the row.
        if ((e.target as HTMLElement).closest("button")) return;
        onShowAccount?.(a.accountId);
      },
      onKeyDown: (e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) onShowAccount?.(a.accountId);
      },
    }),
    [selectedId, onShowAccount],
  );
  const renderCell = useCallback(
    (c: ColumnDef<AccountFacts>, a: AccountFacts): React.ReactNode => {
      switch (c.id) {
        case "main":
          return (
            <button
              type="button"
              className={a.isMain ? "icon-btn icon-btn-xs star-btn on" : "icon-btn icon-btn-xs star-btn"}
              aria-pressed={a.isMain}
              aria-label={a.isMain ? `${a.label} is the main account` : `Make ${a.label} the main account`}
              title={a.isMain ? "Main account" : "Make this the main account"}
              onClick={(e) => {
                e.stopPropagation();
                if (!a.isMain) void settingsSync.update({ main_account_id: a.accountId });
              }}
            >
              <Icon name={a.isMain ? "starFilled" : "star"} size={12} />
            </button>
          );
        case "status":
          return (
            <>
              <span className={`status-dot status-${a.status}`} aria-hidden="true" />
              {a.status === "locked" ? "TradePrizm lock active" : "—"}
            </>
          );
        case "account":
          return (
            <>
              {privacy ? "***" : a.label}
              <span className="muted"> · {a.brokerCurrency ?? "currency unavailable"}</span>
              {a.simulation && <span className="muted"> · sim</span>}
            </>
          );
        case "actions":
          return <AccountFlattenButton accountId={a.accountId} label={privacy ? "***" : a.label} />;
        default:
          return undefined;
      }
    },
    [privacy],
  );
  return (
    <div className="dock-table">
      <div className="table-toolbar">
        <Tabs
          tabs={ACCOUNTS_VIEWS}
          value={mode}
          onChange={(m) => onModeChange?.(m)}
          label="Accounts shown"
          idBase={id}
          variant="pill"
        />
        <span className="muted">
          {rows.length} of {tv.accounts.length} accounts
        </span>
        <span className="spacer" />
        <FlattenAllButton />
        <ColumnPicker def={ACCOUNTS_TABLE} state={state} onChange={setState} />
      </div>
      <div {...tabPanelProps(id, mode)}>
        <DataTable
          def={ACCOUNTS_TABLE}
          state={state}
          onStateChange={setState}
          rows={rows}
          rowKey={(a) => a.accountId}
          multiAccount={false}
          totalLabel="Total"
          empty={{ title: "No Accounts", text: "Connect a Rithmic login to see your accounts here." }}
          ctx={ctx}
          zone={zone}
          rowProps={rowProps}
          renderCell={renderCell}
        />
      </div>
    </div>
  );
}

// ---- Orders ----------------------------------------------------------------------------------

const ORDER_FILTERS = [
  ["all", "All"],
  ["open", "Open"],
  ["filled", "Filled"],
  ["cancelled", "Cancelled"],
] as const;
type OrderFilter = (typeof ORDER_FILTERS)[number][0];

function matches(o: OrderEvent, f: OrderFilter): boolean {
  switch (f) {
    case "all":
      return true;
    case "open":
      return isWorkingOrder(o);
    case "filled":
      return o.state === OrderState.FILLED || o.state === OrderState.PARTIALLY_FILLED;
    case "cancelled":
      return (
        o.state === OrderState.CANCELLED ||
        o.state === OrderState.REJECTED ||
        o.state === OrderState.NOT_PLACED
      );
  }
}

export type AccountFilterProps = {
  account?: AccountFilter;
  onAccountChange?: (f: AccountFilter) => void;
};

export function OrdersTable({
  account = "global",
  onAccountChange,
  ...cols
}: AccountFilterProps & TableColumnsProps): React.JSX.Element {
  const id = useId();
  const [filter, setFilter] = useState<OrderFilter>("all");
  const tv = useTradingViewSlice(ordersSlice, sameOrdersSlice);
  const { scope, multi } = scopeOf(tv, account);
  const privacy = useSettings((s) => s.settings.privacy);
  const zone = useDisplayZone();
  const ctx = useCellCtx<OrderFacts>();
  const [state, setState] = useTable(ORDERS_TABLE, cols);
  const rows = orderFacts(
    tv,
    filterByAccountScope(tv.orders, scope).filter((o) => matches(o, filter)),
  );
  return (
    <div className="dock-table">
      <div className="table-toolbar">
        <Tabs
          tabs={ORDER_FILTERS}
          value={filter}
          onChange={setFilter}
          label="Order status filter"
          idBase={id}
          variant="pill"
        />
        <span className="spacer" />
        {onAccountChange && <AccountFilterSelect filter={account} onChange={onAccountChange} />}
        <button type="button" className="btn btn-sm" disabled title="Export arrives with the journal">
          Export
        </button>
        <ColumnPicker def={ORDERS_TABLE} state={state} onChange={setState} />
      </div>
      <div {...tabPanelProps(id, filter)}>
        <DataTable
          def={ORDERS_TABLE}
          state={state}
          onStateChange={setState}
          rows={rows}
          rowKey={(o) => o.clientOrderId}
          multiAccount={multi}
          grouping={{
            accountOf: (a) => accountLabel(tv, a, privacy),
            order: tv.accounts.map((a) => a.summary.id),
            style: "header",
            noun: "order",
          }}
          empty={{
            title: !tv.bookCurrent
              ? "Orders unconfirmed"
              : filter === "all"
                ? "No Orders"
                : `No ${filter[0]?.toUpperCase() ?? ""}${filter.slice(1)} Orders`,
            text:
              missingText(scope) ??
              (tv.bookCurrent
                ? "Orders you place will appear here."
                : "Waiting for current broker account coverage."),
          }}
          ctx={ctx}
          zone={zone}
          rowProps={orderRowProps}
          renderCell={orderCell}
        />
      </div>
    </div>
  );
}

const orderRowProps = (o: OrderFacts): React.HTMLAttributes<HTMLTableRowElement> => ({
  title: o.rejectReason || undefined,
});

function orderCell(
  c: ColumnDef<OrderFacts & { order: OrderEvent }>,
  o: OrderFacts & { order: OrderEvent },
): React.ReactNode {
  switch (c.id) {
    case "qty":
      return o.filled > 0 && o.filled < o.qty ? `${String(o.filled)}/${String(o.qty)}` : String(o.qty);
    case "status":
      return (
        <>
          {ORDER_STATE[o.order.state] ?? "—"}
          {o.external && <span className="muted"> · external</span>}
        </>
      );
    case "actions":
      return <OrderCancelButton order={o.order} />;
    default:
      return undefined;
  }
}

// ---- Positions -------------------------------------------------------------------------------

const POS_TABS = [
  ["open", "Open"],
  ["fills", "Fills"],
  ["journal", "Journal"],
] as const;
type PosTab = (typeof POS_TABS)[number][0];

/** Position columns that change with the clock (refresh every second while shown). */
const CLOCK_COLUMNS: ReadonlySet<string> = new Set(["duration"]);

/**
 * Positions with Open / Fills / Journal sub-tabs (owner mockup 01). Shows the accounts its
 * filter selects (default: follow the top bar); with several, rows carry an Account column and
 * are grouped by account with per-account subtotals (Q-W1). Columns are adjustable; the default
 * set is Tradovate's (docs/DESIGN-REFERENCE.md "Table columns").
 */
export function PositionsPanel({
  onOpenJournal,
  account = "global",
  onAccountChange,
  ...cols
}: AccountFilterProps & TableColumnsProps & { onOpenJournal?: () => void }): React.JSX.Element {
  const id = useId();
  const [tab, setTab] = useState<PosTab>("open");
  // The whole view: open P&L, marks and excursions change with every quote.
  const tv = useTradingView();
  const { scope, multi } = scopeOf(tv, account);
  const privacy = useSettings((s) => s.settings.privacy);
  const zone = useDisplayZone();
  const ctx = useCellCtx<PositionFacts>();
  const [state, setState] = useTable(POSITIONS_TABLE, cols);
  const [fillState, setFillState] = useTable(FILLS_TABLE, cols);
  const now = useNow(state.columns.some((c) => CLOCK_COLUMNS.has(c)));
  const mine = filterByAccountScope(tv.positions, scope);
  const rows = positionFacts(tv, mine, now);
  const working = filterByAccountScope(tv.orders, scope).filter(isWorkingOrder);
  const scopedAccounts = tv.accounts.filter(
    (a) => scope.kind === "all" || (scope.kind === "one" && a.summary.id === scope.id),
  );
  const bookCurrent = scopedAccounts.length > 0 && scopedAccounts.every((a) => a.openPositions !== null);
  const upnl = !bookCurrent ? null : mine.length === 0 ? 0n : sumCents(mine.map((p) => p.unrealizedCents));
  return (
    <div className="dock-table positions-table">
      <div className="table-toolbar">
        <Tabs tabs={POS_TABS} value={tab} onChange={setTab} label="Positions view" idBase={id} />
        <span className="spacer" />
        {tab === "open" && (
          <span className="muted">
            Position estimate{" "}
            <span className={`num tone-${pnlTone(upnl === null ? null : Number(upnl))}`}>
              {formatCents(scope.kind === "none" ? null : upnl, { privacy, signed: true })}
            </span>
          </span>
        )}
        {onAccountChange && <AccountFilterSelect filter={account} onChange={onAccountChange} />}
        {tab === "open" && (
          <ExitAllButton
            scope={scope}
            label={scope.kind === "one" ? accountLabel(tv, scope.id, privacy) : undefined}
          />
        )}
        {tab === "open" && <ColumnPicker def={POSITIONS_TABLE} state={state} onChange={setState} />}
        {tab === "fills" && <ColumnPicker def={FILLS_TABLE} state={fillState} onChange={setFillState} />}
      </div>
      <div className="positions-content" {...tabPanelProps(id, tab)}>
        {tab === "open" && (
          <>
            <div className="section-label">Positions · {bookCurrent ? mine.length : "unconfirmed"}</div>
            <DataTable
              def={POSITIONS_TABLE}
              state={state}
              onStateChange={setState}
              rows={rows}
              rowKey={(p) => p.key}
              multiAccount={multi}
              grouping={{
                accountOf: (a) => accountLabel(tv, a, privacy),
                order: tv.accounts.map((a) => a.summary.id),
                style: "subtotal",
              }}
              empty={{
                title: bookCurrent ? "No Open Positions" : "Positions unconfirmed",
                text:
                  missingText(scope) ??
                  (bookCurrent
                    ? "Your active trades will appear here once an order is filled."
                    : "An empty list does not mean flat; waiting for broker confirmation."),
              }}
              ctx={ctx}
              zone={zone}
              renderCell={(c, p) => {
                switch (c.id) {
                  case "side":
                    return (
                      <span className={`side-tag ${p.netQty > 0 ? "side-long" : "side-short"}`}>
                        {p.netQty > 0 ? "LONG" : "SHORT"}
                      </span>
                    );
                  case "avgPrice":
                    return p.exactCost ? undefined : (
                      <span title="Average rounded to a tick by the gateway">
                        {formatCell(c, p, ctx).text}
                      </span>
                    );
                  case "maxDrawdown":
                  case "maxRunup":
                    return p.excursion ? (
                      <ExcursionCell
                        text={formatCell(c, p, ctx)}
                        since={formatTime(p.excursion.sinceMs, zone)}
                      />
                    ) : undefined;
                  case "exit":
                    return <PositionCloseButton p={p} account={accountLabel(tv, p.accountId, privacy)} />;
                  default:
                    return undefined;
                }
              }}
            />
            <div className="section-label">
              Working orders · {bookCurrent ? working.length : "unconfirmed"}
            </div>
            {working.length === 0 ? (
              <div className="empty-inline pad-x">
                <span className="muted">{bookCurrent ? "No working orders." : "Orders unconfirmed."}</span>
              </div>
            ) : (
              <ul className="working-list">
                {working.map((o) => (
                  <li key={o.clientOrderId} className="num">
                    {multi && <span className="muted">{accountLabel(tv, o.accountId, privacy)} · </span>}
                    <span className={o.side === Side.BUY ? "tone-pos" : "tone-neg"}>{SIDE[o.side]}</span>{" "}
                    {o.qty - o.filled} {o.symbol} {ORDER_TYPE[o.type]?.toLowerCase()}{" "}
                    {workingPrices(o.limitTicks, o.stopTicks, o.symbol)}
                    <span className="muted"> · {ORDER_STATE[o.state]}</span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {tab === "fills" && (
          <FillsGrid
            fills={filterByAccountScope(tv.fills, scope)}
            multi={multi}
            tv={tv}
            empty={missingText(scope)}
            state={fillState}
            onStateChange={setFillState}
          />
        )}
        {tab === "journal" && (
          <div className="empty-state">
            <div className="empty-title">Trade journal</div>
            <div className="muted">
              Round trips are journaled from fills (Phase 9). Today’s plan and notes live in Daily Trading.
            </div>
            {onOpenJournal && (
              <button type="button" className="btn btn-sm" onClick={onOpenJournal}>
                Open Daily Trading
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** MAE/MFE cell: the tooltip says since when this window has tracked the position. */
function ExcursionCell({ text, since }: { text: CellOut; since: string }): React.JSX.Element {
  return (
    <span className={text.className} title={`Observed by this window since ${since}`}>
      {text.text}
    </span>
  );
}

// ---- Fills -----------------------------------------------------------------------------------

function FillsGrid({
  fills,
  multi,
  tv,
  empty,
  state,
  onStateChange,
}: {
  fills: readonly FillEvent[];
  multi: boolean;
  tv: { accounts: AccountList };
  empty: string | null;
  state: ColumnState;
  onStateChange: (s: ColumnState) => void;
}): React.JSX.Element {
  const zone = useDisplayZone();
  const ctx = useCellCtx<FillFacts>();
  const renderCell = useCallback(
    (c: ColumnDef<FillFacts>, f: FillFacts): React.ReactNode =>
      c.id === "fee" && f.feeIsEstimate && f.feeCents !== 0n ? (
        <i title="Estimated fee">{formatCell(c, f, ctx).text}</i>
      ) : undefined,
    [ctx],
  );
  return (
    <DataTable
      def={FILLS_TABLE}
      state={state}
      onStateChange={onStateChange}
      rows={fillFacts(tv, fills)}
      rowKey={(f) => f.fillId}
      multiAccount={multi}
      empty={{ title: "No Fills", text: empty ?? "Executions will appear here once an order is filled." }}
      ctx={ctx}
      zone={zone}
      renderCell={renderCell}
    />
  );
}

export function FillsTable({
  account = "global",
  onAccountChange,
  ...cols
}: AccountFilterProps & TableColumnsProps): React.JSX.Element {
  const tv = useTradingViewSlice(fillsSlice, sameFillsSlice);
  const { scope, multi } = scopeOf(tv, account);
  const [state, setState] = useTable(FILLS_TABLE, cols);
  const fills = filterByAccountScope(tv.fills, scope);
  return (
    <div className="dock-table">
      <div className="table-toolbar">
        <span className="muted">{fills.length} fills</span>
        <span className="spacer" />
        {onAccountChange && <AccountFilterSelect filter={account} onChange={onAccountChange} />}
        <ColumnPicker def={FILLS_TABLE} state={state} onChange={setState} />
      </div>
      <FillsGrid
        fills={fills}
        multi={multi}
        tv={tv}
        empty={missingText(scope)}
        state={state}
        onStateChange={setState}
      />
    </div>
  );
}

// ---- Notifications ---------------------------------------------------------------------------

const NOTE_COLS = ["Time", "Event", "Symbol", "Details"] as const;

export function NotificationsTable(): React.JSX.Element {
  const [symbol, setSymbol] = useState("");
  return (
    <div className="dock-table">
      <div className="table-toolbar">
        <label className="inline-field">
          <span className="muted">Symbol</span>
          <input
            className="input input-sm"
            placeholder="All"
            value={symbol}
            onChange={(e) => {
              setSymbol(e.target.value.toUpperCase());
            }}
          />
        </label>
        <span className="spacer" />
        <button type="button" className="btn btn-sm" disabled title="Nothing to export yet">
          Export as CSV
        </button>
      </div>
      <table className="grid">
        <Head cols={NOTE_COLS} />
        <tbody>
          <EmptyRow
            span={NOTE_COLS.length}
            title="No Notifications"
            text="Order, position and risk events (Take Profit updated, Position Opened/Closed…) will appear here."
          />
        </tbody>
      </table>
    </div>
  );
}
